import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { regionService, branchService } from '../services/api';
import { Card, Input, Button, NormalMultiSelect, useToast } from './common';
import Breadcrumbs from './common/Breadcrumbs';
import { usePageTitle } from '../context/PageTitleContext';
import useDepartment from '../hooks/useDepartment';

const CreateEditRegion = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const toast = useToast();
  const { setPageTitle } = usePageTitle();
  // IT calls this master a Region, BioMedical an Entity
  const { region: regionWord, regions: regionsWord } = useDepartment();
  const regionLower = regionWord.toLowerCase();
  
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [branchIds, setBranchIds] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [nameError, setNameError] = useState('');
  const [codeError, setCodeError] = useState('');
  const [branchesError, setBranchesError] = useState('');
  
  const isEditMode = !!id && location.pathname.includes('/edit/');
  const isViewMode = !!id && location.pathname.includes('/view/');

  useEffect(() => {
    if (isViewMode) {
      setPageTitle(`View ${regionWord}`);
    } else if (isEditMode) {
      setPageTitle(`Edit ${regionWord}`);
    } else {
      setPageTitle(`Add ${regionWord}`);
    }
  }, [isEditMode, isViewMode, setPageTitle, regionWord]);

  useEffect(() => {
    if (isEditMode || isViewMode) {
      fetchRegionData();
    } else {
      // In create mode, fetch unmapped branches
      fetchBranches();
    }
  }, [id, isEditMode, isViewMode]);

  const fetchBranches = async (currentBranchIds = []) => {
    try {
      setLoadingBranches(true);
      
      // Always fetch unmapped branches
      const unmappedResponse = await branchService.getUnmapped();
      let branchOptions = unmappedResponse.data.data.map(b => ({
        value: b.id,
        label: `${b.code} - ${b.name}`
      }));
      
      // In edit/view mode, also fetch the currently mapped branches for this region
      if ((isEditMode || isViewMode) && currentBranchIds.length > 0) {
        const allBranchesResponse = await branchService.getAll({ noPagination: true });
        const currentlyMapped = allBranchesResponse.data.data.filter(b => 
          currentBranchIds.includes(b.id)
        ).map(b => ({
          value: b.id,
          label: `${b.code} - ${b.name}`
        }));
        
        // Merge: currently mapped branches + unmapped branches
        branchOptions = [...currentlyMapped, ...branchOptions];
      }
      
      setBranches(branchOptions);
    } catch (err) {
      console.error('Failed to load branches:', err);
      toast.error('Failed to load branches');
    } finally {
      setLoadingBranches(false);
    }
  };

  const fetchRegionData = async () => {
    try {
      setLoading(true);
      const response = await regionService.getById(id);
      
      setName(response.data.name);
      setCode(response.data.code || '');
      
      // Get the selected branch IDs
      const selectedBranchIds = response.data.branches && response.data.branches.length > 0
        ? response.data.branches.map(b => b.id)
        : [];
      
      setBranchIds(selectedBranchIds);
      
      // Fetch branches (unmapped + currently mapped to this region)
      await fetchBranches(selectedBranchIds);
    } catch (err) {
      toast.error(`Failed to fetch ${regionLower} details`);
      console.error(err);
      navigate('/admin/regions');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    let isValid = true;

    // Validate region name
    if (!name.trim()) {
      setNameError(`${regionWord} name is required`);
      isValid = false;
    } else {
      setNameError('');
    }

    // Validate region code
    if (!code.trim()) {
      setCodeError(`${regionWord} code is required`);
      isValid = false;
    } else {
      setCodeError('');
    }

    // Validate branches
    if (branchIds.length === 0) {
      setBranchesError('At least one branch must be selected');
      isValid = false;
    } else {
      setBranchesError('');
    }

    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setError('');
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      const payload = {
        name: name.trim(),
        code: code.trim().toUpperCase(),
        branchIds: branchIds
      };

      if (isEditMode) {
        await regionService.update(id, payload);
        toast.success(`${regionWord} updated successfully`);
      } else {
        await regionService.create(payload);
        toast.success(`${regionWord} created successfully`);
      }
      
      navigate('/admin/regions');
    } catch (err) {
      const errorMsg = err.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} ${regionLower}`;
      setError(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/regions');
  };

  if (loading && (isEditMode || isViewMode) && !name) {
    return (
      <div className="admin-panel">
        <Card>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Loading {regionLower} details...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <Card className="w-full">
        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded border-l-4 border-danger-600">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} noValidate className="w-full">
          {/* Breadcrumbs Row */}
          <Breadcrumbs
            items={[
              { label: regionsWord, to: '/admin/regions' },
              { label: isViewMode ? `View ${regionWord}` : isEditMode ? `Edit ${regionWord}` : `Add ${regionWord}` }
            ]}
            className="mb-6"
          />

          {/* Region Code and Name in one line */}
          <div className="flex gap-4 mb-6">
            {/* Region Code */}
            <div className="flex-1">
              <Input
                label={`${regionWord} Code`}
                name="code"
                value={code}
                onChange={(e) => {
                  const upperValue = e.target.value.toUpperCase();
                  setCode(upperValue);
                  if (codeError) setCodeError('');
                }}
                placeholder={`Enter ${regionLower} code (e.g., NORTH, SOUTH)`}
                required
                error={codeError}
                disabled={loading || isViewMode || isEditMode}
                helperText={isEditMode ? `${regionWord} code cannot be changed after creation` : `Enter a unique code for this ${regionLower}`}
              />
            </div>

            {/* Region Name */}
            <div className="flex-1">
              <Input
                label={`${regionWord} Name`}
                name="name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (nameError) setNameError('');
                }}
                placeholder={`Enter ${regionLower} name`}
                required
                error={nameError}
                disabled={loading || isViewMode}
              />
            </div>
          </div>

          {/* Branches Multi-Select */}
          <div className="mb-6">
            <NormalMultiSelect
              label="Branches"
              name="branches"
              value={branchIds}
              onChange={(e) => {
                setBranchIds(e.target.value);
                if (branchesError) setBranchesError('');
              }}
              options={branches}
              placeholder="Select branches"
              required
              error={branchesError}
              disabled={loading || isViewMode}
              loading={loadingBranches}
            />
          </div>

          {/* Actions Row: Buttons at far right */}
          {isViewMode ? (
            <div className="flex w-full justify-end gap-3 mt-8 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="border-primary-600 text-primary-600 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Back
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={() => navigate(`/admin/regions/edit/${id}`)}
                className="focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Edit {regionWord}
              </Button>
            </div>
          ) : (
            <div className="flex w-full justify-end gap-3 mt-8 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
                className="border-primary-600 text-primary-600 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
                className="focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                {loading ? 'Saving...' : isEditMode ? `Update ${regionWord}` : `+ Add ${regionWord}`}
              </Button>
            </div>
          )}
        </form>
      </Card>
    </div>
  );
};

export default CreateEditRegion;
